import { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Stack,
  Group,
  Select,
  Text,
  Alert,
  SegmentedControl,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Info } from 'react-feather';
import { coachesApi } from '../../services/api/coaches';
import { partnersApi } from '../../services/api/partners';
import type { Coach, CreateCoachRequest, CoachType, Partner } from '../../types';
import { TimezoneSelect } from '../common';
import { formValidators } from '../../utils/validation';
import { getApiErrorMessage } from '../../services/api/client';
import {
  coachEmailErrorField,
  validateMedleyEmail,
  validatePersonalEmail,
} from '../../utils/medleyEmail';
import { CoachEmailFields } from './CoachEmailFields';

interface AddCoachModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: (coach: Coach) => void;
}

export function AddCoachModal({ opened, onClose, onSuccess }: AddCoachModalProps) {
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);

  const form = useForm<CreateCoachRequest>({
    // MED-1355: check each email field when it loses focus, so a wrong
    // domain shows before the user submits.
    validateInputOnBlur: ['preferred_email', 'email'],
    initialValues: {
      first_name: '',
      last_name: '',
      preferred_email: '',
      email: '',
      coaching_specializations: '',
      location: '',
      home_base_timezone: '',
      coach_type: 'medley',
      partner_id: undefined,
    },
    validate: {
      first_name: formValidators.required('First name'),
      last_name: formValidators.required('Last name'),
      // MED-1355: same domain rules as the API.
      preferred_email: validatePersonalEmail,
      email: validateMedleyEmail,
      location: formValidators.required('Location'),
      home_base_timezone: formValidators.required('Timezone'),
      partner_id: (value, values) => {
        if (values.coach_type === 'partner' && !value) {
          return 'Partner organization is required for Partner Coaches';
        }
        return null;
      },
    },
  });

  const coachType = form.values.coach_type || 'medley';

  // Fetch partners when modal opens and coach type is partner
  useEffect(() => {
    if (opened && coachType === 'partner' && partners.length === 0) {
      setLoadingPartners(true);
      partnersApi
        .list({ limit: 100 })
        .then((response) => {
          setPartners(response.data);
        })
        .catch(() => {
          notifications.show({
            title: 'Error',
            message: 'Failed to load partner organizations',
            color: 'red',
          });
        })
        .finally(() => setLoadingPartners(false));
    }
  }, [opened, coachType, partners.length]);

  const handleCoachTypeChange = (value: string) => {
    form.setFieldValue('coach_type', value as CoachType);
    // Clear partner_id when switching to medley
    if (value === 'medley') {
      form.setFieldValue('partner_id', undefined);
      form.clearFieldError('partner_id');
    }
  };

  const handleSubmit = async (values: CreateCoachRequest) => {
    try {
      setLoading(true);

      // MED-1355: no pre-check here. The API decides if the Medley email is
      // free, and an existing user with no coach profile gets one. Its
      // errors carry a code for the field.
      // Clean up payload — don't send partner_id for medley coaches
      const payload: CreateCoachRequest = { ...values };
      if (payload.coach_type === 'medley' || !payload.coach_type) {
        delete payload.partner_id;
      }

      const coach = await coachesApi.create(payload);
      // MED-1355: a Medley email that belongs to an existing account gets a
      // coach profile on that account, and the account keeps its own name.
      // Tell the user when that name is not the one they typed.
      const typedName = `${values.first_name.trim()} ${values.last_name.trim()}`;
      const savedName = `${coach.first_name ?? ''} ${coach.last_name ?? ''}`.trim();
      const usedAccountName = savedName !== '' && savedName !== typedName;
      notifications.show({
        title: 'Success',
        message: usedAccountName
          ? `Coach added to the existing account for this Medley email. The profile uses the account name: ${savedName}.`
          : 'Coach added successfully',
        color: 'green',
      });
      form.reset();
      onSuccess(coach);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to add coach');
      const field = coachEmailErrorField(error);
      if (field) {
        form.setFieldError(field, message);
        return;
      }
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const partnerOptions = partners.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  return (
    <Modal opened={opened} onClose={handleClose} title="Add New Coach" size="lg">
      {/* noValidate: the form rules show each error under its field, never
          the browser's own bubble. */}
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text c="dimmed" size="sm">
            Enter the required information to create a coach profile
          </Text>

          {/* MED-683: status is derived. A coach is Active once a modality is
              approved on the profile. Partner coaches get Group Coaching
              approved at create, so they start Active. */}
          <Alert variant="light" color="gray">
            <Text size="sm">
              New Medley coaches start as <Text span fw={700}>Inactive</Text>. They become
              Active when a modality is approved on their profile. Partner coaches start
              as Active.
            </Text>
          </Alert>

          {/* Coach Type Selector (AC1) */}
          <div>
            <Text size="sm" fw={500} mb={4}>
              Coach Type
            </Text>
            <SegmentedControl
              fullWidth
              data={[
                { label: 'Medley Coach', value: 'medley' },
                { label: 'Partner Coach', value: 'partner' },
              ]}
              value={coachType}
              onChange={handleCoachTypeChange}
            />
          </div>

          {/* Partner Organization Dropdown (AC2, AC3, AC4) */}
          {coachType === 'partner' && (
            <Select
              label="Partner Organization"
              placeholder="Select partner organization..."
              required
              data={partnerOptions}
              searchable
              disabled={loadingPartners}
              {...form.getInputProps('partner_id')}
            />
          )}

          <Group grow align="flex-start">
            <TextInput
              label="First Name"
              placeholder="e.g., Sarah"
              required
              {...form.getInputProps('first_name')}
            />
            <TextInput
              label="Last Name"
              placeholder="e.g., Chen"
              required
              {...form.getInputProps('last_name')}
            />
          </Group>

          <CoachEmailFields
            personalInputProps={form.getInputProps('preferred_email')}
            medleyInputProps={form.getInputProps('email')}
          />

          <Textarea
            label="Coaching Specializations"
            placeholder="e.g., Executive leadership, Career transitions, Team dynamics"
            minRows={3}
            {...form.getInputProps('coaching_specializations')}
          />

          <TextInput
            label="Location"
            placeholder="e.g., San Francisco, CA, USA"
            required
            {...form.getInputProps('location')}
          />

          <TimezoneSelect
            label="Home-base Timezone"
            placeholder="Select timezone..."
            required
            {...form.getInputProps('home_base_timezone')}
          />

          <Alert variant="light" color="blue" icon={<Info size={16} />} p="sm">
            <Text size="sm">
              Additional profile details (phone, demographics, industries, etc.) can be added
              after creating the coach profile.
            </Text>
          </Alert>

          <Group justify="flex-end" className="modal-footer">
            <Button variant="default" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Create Coach
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default AddCoachModal;
